/**
 * @fileoverview Shared asset hierarchy helpers.
 *
 * This is the CANONICAL SOURCE for these helpers.
 * The frontend copies this file via `pnpm sync:types`.
 * Do not edit the frontend copy directly.
 *
 * The functions here are pure — no Yjs / Zustand / I/O. Callers supply snapshots
 * of assets and jobs as plain Maps. Frontend-specific concerns (e.g., merging in
 * the submission processor queue) are layered on top at the call site.
 */

import type { AssetOperation } from './shared.js'

/** Deterministic still-image children, sharing parent-space editing and lineage. */
export function isDerivedStillImage(operation: string | null | undefined): boolean {
  return operation === 'crop' || operation === 'crop-adjust'
}

/**
 * Minimal asset shape the hierarchy kernel needs.
 * A real Asset has many more fields; this keeps the shared module decoupled.
 */
export interface HierarchyAssetInfo {
  operation?: AssetOperation
  parentId?: string | null
  sceneId?: string | null
  siblingIndex?: number
  shotNumber?: number
}

/**
 * Minimal job shape the hierarchy kernel needs.
 */
export interface HierarchyJobInfo {
  operation?: AssetOperation
  parentId?: string | null
  sceneId?: string | null
  siblingIndex?: number
  shotNumber?: number
}

/**
 * Minimal subject shape for shot-number tracking.
 */
export interface HierarchySubjectInfo {
  shotNumber?: number
}

/**
 * Minimal offshoot-tracking shape.
 */
export interface HierarchyOffshootInfo {
  operation?: AssetOperation
  parentId?: string | null
  sceneId?: string | null
  offshootNumber?: number
}

/**
 * Predicate: "does this operation count as a root-level take for sibling numbering?"
 *
 * The backend has two legitimate definitions of "root":
 *   - Frontend Take-numbering & MCP upload flow: t2v | i2v | upload | video-frame
 *   - Board `create_node` layout / placement:    t2v | i2v | upload | generate-image | i2s
 *
 * Callers must pass the predicate they want. Two prebuilt ones are exported.
 */
export type IsRootOperation = (operation: AssetOperation | undefined) => boolean

/**
 * Root operations for the take-numbering / upload flow. Matches the frontend
 * Asset Take-number semantics; also used by the MCP upload tools.
 */
export const isRootOperation: IsRootOperation = operation =>
  operation === 't2v' || operation === 'i2v' || operation === 'upload' || operation === 'video-frame'

/**
 * Root operations as seen by the board `create_node` / `create_nodes` path,
 * which treats image and i2s operations as root for layout placement.
 */
export const isBoardRootOperation: IsRootOperation = operation =>
  operation === 't2v' ||
  operation === 'i2v' ||
  operation === 'upload' ||
  operation === 'generate-image' ||
  operation === 'i2s' ||
  operation === 'generate-audio'

/**
 * Check if an operation is an "offshoot-like" child operation.
 * Both i2v-with-parent (grabbed frame) and offshoot (Reuse Prompt) display as "Offshoot N"
 * and share sibling numbering and row grouping.
 */
export function isOffshootLike(operation: AssetOperation | undefined, parentId: string | null | undefined): boolean {
  return operation === 'offshoot' || (operation === 'i2v' && parentId != null)
}

/**
 * Pure sibling-index kernel.
 *
 * Computes the next siblingIndex for a new asset/job given iterables of all
 * existing assets and jobs. Root-level takes (t2v + i2v + upload) share a
 * counter per scene; child-level uses per-operation counters under each parent.
 *
 * Accepts any iterable so callers can pass `map.values()`, `Object.values(record)`,
 * an array, etc.
 *
 * Callers that need to account for not-yet-committed state (e.g., a frontend
 * submission queue) should compute that separately and take the max with the
 * return value of this function.
 */
export function computeNextSiblingIndex(
  operation: AssetOperation,
  parentId: string | null,
  sceneId: string | null,
  assets: ReadonlyMap<string, HierarchyAssetInfo>,
  jobs: ReadonlyMap<string, HierarchyJobInfo>,
  isRoot: IsRootOperation = isRootOperation,
): number {
  const isRootTake = parentId === null && isRoot(operation)
  const isOffshootLikeOp = isOffshootLike(operation, parentId)

  const matches = (
    candidateOp: AssetOperation | undefined,
    candidateParent: string | null | undefined,
    candidateScene: string | null | undefined,
  ): boolean => {
    if (candidateOp === undefined) return false

    if (isRootTake) {
      return (candidateParent ?? null) === null && (candidateScene ?? null) === sceneId && isRoot(candidateOp)
    }

    if (isOffshootLikeOp) {
      return (candidateParent ?? null) === parentId && isOffshootLike(candidateOp, candidateParent ?? null)
    }

    return (candidateParent ?? null) === parentId && candidateOp === operation
  }

  let maxIndex = 0
  for (const asset of assets.values()) {
    if (matches(asset.operation, asset.parentId, asset.sceneId)) {
      if ((asset.siblingIndex ?? 0) > maxIndex) maxIndex = asset.siblingIndex ?? 0
    }
  }
  for (const job of jobs.values()) {
    if (matches(job.operation, job.parentId, job.sceneId)) {
      if ((job.siblingIndex ?? 0) > maxIndex) maxIndex = job.siblingIndex ?? 0
    }
  }

  return maxIndex + 1
}

/**
 * Get the current maximum shot number across all assets, jobs, and (optionally) subject canvas nodes.
 *
 * Used as a hint for the backend's lazy migration: on first allocation for an
 * existing project, the backend bootstraps its counter past any locally-assigned
 * numbers.
 */
export function getCurrentMaxShotNumber(
  assets: ReadonlyMap<string, HierarchyAssetInfo>,
  jobs: ReadonlyMap<string, HierarchyJobInfo>,
  subjects?: ReadonlyMap<string, HierarchySubjectInfo>,
): number {
  let maxShotNumber = 0

  for (const asset of assets.values()) {
    if (asset.shotNumber !== undefined && asset.shotNumber > maxShotNumber) {
      maxShotNumber = asset.shotNumber
    }
  }
  for (const job of jobs.values()) {
    if (job.shotNumber !== undefined && job.shotNumber > maxShotNumber) {
      maxShotNumber = job.shotNumber
    }
  }
  if (subjects) {
    for (const subject of subjects.values()) {
      if (subject.shotNumber !== undefined && subject.shotNumber > maxShotNumber) {
        maxShotNumber = subject.shotNumber
      }
    }
  }

  return maxShotNumber
}

/**
 * Scene-global offshoot counter.
 *
 * Unlike siblingIndex (per-parent), offshootNumber is scene-scoped. All
 * offshoots in a scene share a single counter: 1, 2, 3… Covers both `offshoot`
 * and i2v-with-parent operations (both render as "Offshoot N").
 */
export function getNextOffshootNumber(
  sceneId: string,
  assets: ReadonlyMap<string, HierarchyOffshootInfo>,
  jobs: ReadonlyMap<string, HierarchyOffshootInfo>,
): number {
  let maxNumber = 0

  for (const asset of assets.values()) {
    if (asset.sceneId === sceneId && isOffshootLike(asset.operation, asset.parentId ?? null)) {
      maxNumber = Math.max(maxNumber, asset.offshootNumber ?? 0)
    }
  }
  for (const job of jobs.values()) {
    if (job.sceneId === sceneId && isOffshootLike(job.operation, job.parentId ?? null)) {
      maxNumber = Math.max(maxNumber, job.offshootNumber ?? 0)
    }
  }

  return maxNumber + 1
}

/**
 * Calculate the variation number for an asset/job.
 *
 * Row-creators always get v1:
 * - Root takes: t2v, i2v, upload
 * - Child takes: extend
 * - Grouped operations: first v2v, first offshoot/i2v-with-parent (subsequent join the row as v2, v3...)
 *
 * Variations use siblingIndex + 1 (v2, v3, ...) since the parent row creator is v1.
 */
export function calculateVariationNumber(
  operation: AssetOperation,
  siblingIndex: number,
  parentId?: string | null,
): number {
  if (operation === 'v2v' || isOffshootLike(operation, parentId ?? null)) {
    return siblingIndex
  }
  if (operation === 'variation' || (operation === 'generate-image' && parentId)) {
    return siblingIndex + 1
  }
  return 1
}
